import { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Button, Chip, Grid, LinearProgress, Stack, CircularProgress, Accordion, AccordionSummary, AccordionDetails, Paper, alpha, IconButton } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Description as FileIcon, Add as AddIcon, Error as ErrorIcon } from '@mui/icons-material';
import { documentGet, fileSave, communicationGet, documentSave } from 'store/application/student-enrollment';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getTypes } from 'store/lookup';
import { useTheme } from '@emotion/react';
import { DeleteIcon } from 'components/Icons';
import * as snackbar from 'utils/snackbar';

const StatusBadge = ({ status, t }) => {
  const theme = useTheme();
  const styles = {
    no_file: { color: 'default', label: 'Incomplete', bgcolor: alpha(theme.palette.background.default, 0.9) },
    pending: { color: 'primary', label: 'In Review', bgcolor: alpha(theme.palette.secondary.main, 0.4) },
    approved: { color: 'success', label: 'Approved', bgcolor: alpha(theme.palette.success.main, 0.4) },
    rejected: { color: 'error', label: 'Action Required', bgcolor: alpha(theme.palette.error.main, 0.4) },
  };

  let displayStatus = status;
  if (status === 'pending') {
    displayStatus = 'pending';
  }

  const current = styles[displayStatus] || styles.no_file;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {displayStatus === 'rejected' && (
        <Typography variant="caption" color="error" sx={{ fontWeight: 600, display: { xs: 'none', sm: 'block' } }}>
          {t('Check Feedback')}
        </Typography>
      )}
      <Chip
        label={current.label}
        size="small"
        sx={{
          bgcolor: current.bgcolor,
          color: current.color === 'default' ? '#8590a3ff' : undefined,
          fontWeight: 700,
          minWidth: 80
        }}
      />
    </Box>
  );
};

export default function EnrollmentDocumentUpload({ enrollment }) {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState('panel1');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const documentTypes = useSelector(state => state.lookup.types?.filter(t => t.category === 'enrollmentDocument') || []);
  const theme = useTheme();
  const applicationStatus = enrollment?.applicationSchool?.status

  useEffect(() => {
    dispatch(getTypes());
  }, [dispatch]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const encodedId = btoa(encodeURIComponent(enrollment?.id));
      const result = await dispatch(documentGet({ id: encodedId }));
      setLoading(false);
      const response = result?.payload || result;

      let docsArray = [];
      if (response) {
        if (Array.isArray(response)) {
          docsArray = response;
        } else if (typeof response === 'object' && response.data) {
          docsArray = Array.isArray(response.data) ? response.data : [response.data];
        }
      }
      if (docsArray.length) {
        const mappedDocuments = await Promise.all(
          docsArray.map(async (doc, index) => {
            let notes = '';
            try {
              const commRes = await dispatch(communicationGet({ id: doc.id }));
              const commData = commRes?.payload || commRes;
              if (Array.isArray(commData)) {
                notes = commData
                  .filter(
                    c => c.enrollmentPackageDocumentId === doc.id || c.enrollmentPackageDocumentId === doc.apiId
                  )
                  .map(c => c.explanation)
                  .filter(Boolean);
              } else if (commData && commData.explanation) {
                notes = [commData.explanation];
              }
            } catch (err) {
              console.log('Communication fetch error, proceeding without note.');
            }

            const docType = documentTypes.find(dt => dt.code === doc.type);

            return {
              id: `panel${index + 1}`,
              category: doc.type || '',
              name: docType?.name || `Document ${index + 1}`,
              status: doc.status || 'pending',
              description: <div style={{ whiteSpace: "pre-line" }}>{docType?.value?.instructions || 'Please upload the required document.'}</div>,
              isUploading: false,
              files: doc.enrollmentPackageFiles || [],
              apiId: doc.id,
              notes,
              sequence: docType?.seq ?? 9999
            };
          })
        );

        const sortedDocuments = mappedDocuments.sort((a, b) => a.sequence - b.sequence);
        setDocuments(sortedDocuments);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  useEffect(() => {
    const loadDocuments = async () => {
      if (enrollment?.id && documentTypes.length > 0) {
        await fetchDocuments();
        const encodedId = btoa(encodeURIComponent(enrollment?.id));
        const result = await dispatch(documentGet({ id: encodedId }));
        const response = result?.payload || result;

        let docsArray = [];
        if (response) {
          if (Array.isArray(response)) {
            docsArray = response;
          } else if (typeof response === 'object' && response.data) {
            docsArray = Array.isArray(response.data) ? response.data : [response.data];
          }
        }

        if (!docsArray.length) {
          await dispatch(documentSave({ enrollmentPackageId: enrollment?.id }));
          await fetchDocuments();
        }
      }
    };
    loadDocuments();
  }, [enrollment?.id, documentTypes.length]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleFileUpload = async (id) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,application/pdf';

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (file.size > 10 * 1024 * 1024) {
        snackbar.error(t('File size exceeds the 10MB limit. Please upload a smaller file.'));
        setDocuments(prev => prev.map(doc => doc.id === id ? { ...doc, isUploading: false } : doc));
        return;
      }

      setDocuments(prev => prev.map(d => d.id === id ? { ...d, isUploading: true } : d));

      try {
        const formData = new FormData();
        formData.append('afile', file, file.name);


        const uploadRes = await fetch('/api/file', {
          method: 'POST',
          body: formData,
        });
        const uploadData = await uploadRes.json();

        await dispatch(fileSave({
          enrollmentPackageDocumentId: documents.find(doc => doc.id === id)?.apiId,
          fileId: uploadData.id,
        }));

        setDocuments(prev => prev.map(doc => {
          if (doc.id === id) {
            return {
              ...doc,
              files: [...doc.files, { name: file.name, id: uploadData.id }],
              isUploading: false,
              status: (doc.status === 'no_file' || doc.status === 'rejected') ? 'pending' : doc.status
            };
          }
          return doc;
        }));
      } catch (error) {
        setDocuments(prev => prev.map(doc => doc.id === id ? { ...doc, isUploading: false } : doc));
        console.error('File upload failed:', error);
      }

      try {
        await dispatch(documentSave({
          id: documents.find(doc => doc.id === id)?.apiId,
          status: 'pending'
        }))
      } catch (error) {
        console.error('Error updating document status:', error);
      }
    };

    input.click();
  };

  const handleRemoveFile = async (docId, file) => {
    const doc = documents.find(d => d.id === docId);
    const fileToDelete = doc?.files.find(f => (f?.file?.name || f.name) === (file?.file?.name || file.name));

    if (!fileToDelete) return;

    try {
      await dispatch(fileSave({
        id: file.id,
        deleted: true,
      }));

      setDocuments(prev => prev.map(d => {
        if (d.id === docId) {
          const updatedFiles = d.files.filter(f => f.id !== file.id);
          return {
            ...d,
            files: updatedFiles,
          };
        }
        return d;
      }));

      const updatedDoc = documents.find(d => d.id === docId);
      const remainingFiles = updatedDoc?.files.filter(f => f.id !== file.id) || [];
      if (remainingFiles.length === 0) {
        await dispatch(documentSave({
          id: doc.apiId,
          status: 'no_file',
        }));
        setDocuments(prev => prev.map(d => {
          if (d.id === docId) {
            return {
              ...d,
              status: 'no_file',
            };
          }
          return d;
        }));
      }

      snackbar.success(t('File deleted successfully'));
    } catch (error) {
      console.error('Error deleting file:', error);
      snackbar.error(t('Failed to delete file. Please try again.'));
    }
  };

  if (!documentTypes.length) return <LinearProgress sx={{ mt: 2 }} />;

  const completedCount = documents.filter(d =>
    d.status === 'approved' || (d.files.length > 0 && d.status !== 'rejected')
  ).length;
  const progress = (completedCount / documents.length) * 100;

  return (
    <Paper sx={{ width: "100%", mx: 'auto', p: 4, borderRadius: 2 }}>
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" align="center" sx={{ mb: 2 }}>
          {applicationStatus === 'rejected' ? t('Application Decision') : t('Enrollment Information Received')}
        </Typography>

        {applicationStatus === 'rejected' ? (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight="bold" align="center" gutterBottom>
              {t('Your application has been rejected')}
            </Typography>
            <Typography variant="body1" align="center">
              {t('Please contact the school main office if you have any questions.')}
            </Typography>
          </Box>
        ) : (
          <>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight="bold" align="center" gutterBottom>
            {t('The following documents are required to complete your enrollment')}
          </Typography>
          <Card variant="outlined" sx={{ bgcolor: 'background.paper', border: 'none', mt: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">{t('Document Completion Progress')}</Typography>
                <Typography variant="subtitle2">{completedCount} / {documents.length} {t('Completed')}</Typography>
              </Box>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 5 }} />
            </CardContent>
          </Card>
        </Box>

        {loading &&
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <CircularProgress sx={{ mb: 2 }} />
          </Box>}
        {/* Accordion List */}
        <Stack spacing={2}>
          {documents.map((doc) => (
            <Accordion
              key={doc.id}
              expanded={expanded === doc.id}
              onChange={handleAccordionChange(doc.id)}
              variant="outlined"
              sx={{
                borderRadius: '8px !important',
                borderColor: doc.status === 'rejected' ? 'error.light' : '#e5e7eb',
                '&:before': { display: 'none' },
                boxShadow: expanded === doc.id ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none'
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>

                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        bgcolor: 'background.paper',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontWeight: 600,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                      }}
                    >
                      {doc.name}
                    </Typography>

                    <Box>
                      {doc.files.length > 0 && !expanded && (
                        <Typography variant="caption" color="text.secondary">
                          {doc.files.length} {t('file(s) uploaded')}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <StatusBadge status={doc.status} t={t} />
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ borderTop: '1px solid background.paper', bgcolor: 'background.paper', p: 3 }}>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={5}>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {doc.description}
                    </Typography>

                    {Array.isArray(doc.notes) && doc.notes.length > 0 && (
                      <Box sx={{ bgcolor: doc.status === 'rejected' ? alpha(theme.palette.error.main, 0.1) : 'transparent', p: 2, borderRadius: 2, border: doc.status === 'rejected' ? `1px solid ${alpha(theme.palette.error.main, 0.1)}` : 'none', display: 'flex', gap: 1.5, flexDirection: 'column' }}>
                        {doc.status === 'rejected' && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ErrorIcon color="error" fontSize="small" sx={{ mt: 0.3 }} />
                            <Typography variant="subtitle2" color="error" fontWeight="bold">{t('Action Required')}</Typography>
                          </Box>
                        )}
                        <Box sx={{ pl: 2, mb: 0 }}>
                          {doc.notes.map((note, idx) => (
                            <Box key={idx}>
                              <Typography variant="caption" color={doc.status === 'rejected' ? 'error.main' : 'textPrimary'}>{idx + 1}) {note}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12} md={7}>
                    <Stack spacing={1.5}>
                      {doc.files.map((file, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: 'background.paper',
                            p: 1.5,
                            borderRadius: 2,
                            border: '1px solid divider',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                          }}
                        >
                          <FileIcon sx={{ color: 'primary.main', mr: 2 }} />
                          <Typography variant="body2" noWrap sx={{ flex: 1, fontWeight: 500 }}>
                            {file?.file?.name || file.name}
                          </Typography>

                          {doc.status !== 'approved' && (
                            <IconButton size="small" onClick={() => handleRemoveFile(doc.id, file)}>
                              <DeleteIcon fontSize="small" color="action" />
                            </IconButton>
                          )}
                        </Box>
                      ))}

                      {doc.isUploading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                          <CircularProgress size={20} sx={{ mr: 2 }} />
                          <Typography variant="body2" color="text.secondary">{t('Uploading file...')}</Typography>
                        </Box>
                      )}

                      {doc.status !== 'approved' && !doc.isUploading && (
                        <Button
                          variant="dashed"
                          startIcon={<AddIcon />}
                          onClick={() => handleFileUpload(doc.id)}
                          sx={{
                            border: '2px dashed',
                            borderColor: 'divider',
                            bgcolor: 'transparent',
                            color: 'text.secondary',
                            justifyContent: 'flex-start',
                            pl: 2,
                            '&:hover': { border: '2px dashed', borderColor: 'divider', bgcolor: 'background.paper' }
                          }}
                        >
                          {t('Click to attach file')}
                        </Button>
                      )}
                    </Stack>
                  </Grid>
                </Grid>

              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
          </>
        )}

      </Box>
    </Paper>
  );
}